import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, Button, Container } from '@mui/material';
import { Videocam as VideocamIcon } from '@mui/icons-material';
import ScheduledCallTwilioRoom from '../../components/ScheduledCall/ScheduledCallTwilioRoom';
import { useUser } from '../../contexts/UserContext';
import { useScheduledCallVideo } from '../../contexts/ScheduledCallVideoContext';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

const ScheduledCallRoomPage: React.FC = () => {
   const { roomId } = useParams<{ roomId: string }>();
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const {
      videoCallState,
      startScheduledCall,
      endScheduledCall,
      leaveScheduledCall,
      isOnScheduledCallPage,
   } = useScheduledCallVideo();

   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [callDetails, setCallDetails] = useState<any>(null);

   // Check if we already have an active video call for this room
   const hasActiveCall = videoCallState.isActive && videoCallState.roomId === roomId;

   useEffect(() => {
      const fetchCallDetails = async () => {
         try {
            if (!roomId) {
               setError('Call not found');
               setLoading(false);
               return;
            }

            console.log('DEBUG: Fetching call details for roomId:', roomId);

            // Find the scheduled call by document ID (roomId is the Firestore document ID)
            const callRef = doc(db, 'scheduledCalls', roomId);
            const callDoc = await getDoc(callRef);

            if (callDoc.exists()) {
               const callData = { id: callDoc.id, ...callDoc.data() };
               console.log('DEBUG: Found call data:', callData);
               setCallDetails(callData);
            } else {
               console.log('DEBUG: No call document found for ID:', roomId);
               setError('Call not found');
            }
         } catch (err: any) {
            console.error('DEBUG: Error fetching call details:', err);
            setError('Failed to fetch call details');
         } finally {
            setLoading(false);
         }
      };
      fetchCallDetails();
   }, [roomId]);

   const handleJoinRoom = () => {
      if (!currentUser || !callDetails || !roomId) {
         setError('Please log in to join the video call');
         return;
      }

      console.log('DEBUG: Starting scheduled call with:', {
         roomName: callDetails.virtualRoomName,
         callTitle: callDetails.title,
         callId: roomId,
         roomId: roomId, // Use document ID for navigation consistency
         role: callDetails.organizerId === currentUser?.uid ? 'host' : 'participant',
         identity: currentUser?.displayName || currentUser?.email || 'Anonymous',
      });

      // Start the scheduled call using the global context
      startScheduledCall({
         roomName: callDetails.virtualRoomName,
         callTitle: callDetails.title,
         callId: roomId,
         roomId: roomId, // Use document ID for navigation consistency
         role: callDetails.organizerId === currentUser?.uid ? 'host' : 'participant',
         identity: currentUser?.displayName || currentUser?.email || 'Anonymous',
         onCallEnd: () => {
            navigate('/dashboard');
         },
      });
   };

   const handleLeaveRoom = () => {
      leaveScheduledCall(); // Use leaveScheduledCall instead of endScheduledCall
      navigate('/dashboard');
   };

   const handleError = (error: Error) => {
      setError(`Failed to join room: ${error.message}`);
   };

   if (loading) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
               <CircularProgress sx={{ mb: 2 }} />
               <Typography>Loading event information...</Typography>
            </Box>
         </Container>
      );
   }

   if (error) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
            <Button variant='contained' onClick={() => navigate('/dashboard')}>
               Back to Dashboard
            </Button>
         </Container>
      );
   }

   if (!callDetails) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Alert severity='warning'>Call information not available</Alert>
         </Container>
      );
   }

   // If we're on the scheduled call page and have an active call, show fullscreen
   if (hasActiveCall && isOnScheduledCallPage) {
      return (
         <Container maxWidth='xl' sx={{ py: 2 }}>
            <ScheduledCallTwilioRoom
               role={videoCallState.role}
               roomName={videoCallState.roomName}
               eventTitle={videoCallState.callTitle}
               onLeave={handleLeaveRoom}
               onError={handleError}
               isPopupMode={false}
            />
         </Container>
      );
   }

   // If we have an active call but we're not on the scheduled call page, show notice
   if (hasActiveCall && !isOnScheduledCallPage) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
               <Typography variant='h5' gutterBottom>
                  You're already in this video call
               </Typography>
               <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                  The video call is active in popup mode. It will return to fullscreen when you're
                  on this page.
               </Typography>
               <Button variant='contained' onClick={() => window.location.reload()}>
                  Refresh to see fullscreen
               </Button>
            </Paper>
         </Container>
      );
   }

   // Show the join room interface
   return (
      <Container maxWidth='md' sx={{ py: 4 }}>
         <Paper sx={{ p: 4, textAlign: 'center' }}>
            <VideocamIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />

            <Typography variant='h4' gutterBottom>
               {callDetails.title}
            </Typography>

            <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
               You're about to join a scheduled video call
            </Typography>

            <Box sx={{ mb: 3 }}>
               {currentUser && (
                  <Typography variant='body2'>
                     <strong>Joining as:</strong>{' '}
                     {currentUser.displayName || currentUser.email || 'User'}
                  </Typography>
               )}
            </Box>

            {!currentUser ? (
               <Alert severity='warning' sx={{ mb: 3 }}>
                  Please log in to join this call
               </Alert>
            ) : callDetails.status === 'cancelled' ? (
               <Alert severity='error' sx={{ mb: 3 }}>
                  This call has been cancelled.
               </Alert>
            ) : null}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
               <Button
                  variant='contained'
                  size='large'
                  startIcon={<VideocamIcon />}
                  onClick={handleJoinRoom}
                  disabled={!currentUser || callDetails.status === 'cancelled'}
               >
                  Join Call
               </Button>

               <Button variant='outlined' size='large' onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
               </Button>
            </Box>

            <Typography variant='caption' display='block' sx={{ mt: 3, color: 'text.secondary' }}>
               Make sure your camera and microphone are ready before joining
            </Typography>
         </Paper>
      </Container>
   );
};

export default ScheduledCallRoomPage;
